import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, X, PlusCircle, Save, Loader2, Activity, Video, ExternalLink, Building2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

const AppointmentTypeSetup = () => {
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = `${window.location.protocol}//${window.location.hostname}/api/appointment-types`;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL, { headers });
      setAppointmentTypes(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch appointment types');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleUpdate = async (id, updatedData) => {
    const toastId = toast.loading('Updating session type...');
    try {
      await axios.put(`${API_URL}/${id}`, updatedData, { headers });
      toast.success('Session type updated!', { id: toastId });
      fetchTypes();
    } catch (err) {
      toast.error('Failed to update', { id: toastId });
    }
  };

  const deleteType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this session type?')) return;
    const toastId = toast.loading('Deleting session type...');
    try {
      await axios.delete(`${API_URL}/${id}`, { headers });
      toast.success('Session type deleted!', { id: toastId });
      fetchTypes();
    } catch (err) {
      toast.error('Failed to delete', { id: toastId });
    }
  };

  const addType = async () => {
    const toastId = toast.loading('Adding new session type...');
    try {
      const newType = {
        code: `TYP-${appointmentTypes.length + 101}`,
        active: true,
        typeName: 'New Appointment Type',
        allBranches: true,
        branches: [],
        virtual: false
      };
      await axios.post(API_URL, newType, { headers });
      toast.success('New session type added!', { id: toastId });
      fetchTypes();
    } catch (err) {
      toast.error('Failed to add', { id: toastId });
    }
  };

  if (loading && appointmentTypes.length === 0) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
    </div>
  );

  return (
    <div className="p-6 bg-[#F8FAFC] min-h-screen text-slate-700 font-sans">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Compact Header */}
        <div className="bg-white px-8 py-6 rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/40 flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-purple-500 via-indigo-500 to-sky-500" />
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200">
                    <Activity className="w-6 h-6 text-white" />
                </div>
                <div>
                    <h1 className="text-2xl font-black text-slate-800 tracking-tight">Appointment Type Setup</h1>
                    <p className="text-[11px] text-slate-400 font-black uppercase tracking-widest mt-1">Configure consultation session categories</p>
                </div>
            </div>
            <button 
                onClick={addType}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-black text-white rounded-2xl text-[12px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100 transition-all active:scale-95 group"
            >
                <PlusCircle className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                Add New Logic Type
            </button>
        </div>

        {/* Professional Table Grid */}
        <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden text-[11px]">
          <table className="min-w-full divide-y divide-slate-100 text-left">
            <thead className="bg-slate-50/50">
              <tr>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest">Code</th>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest">Status</th>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest">Session Type Label</th>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-center">Global Access</th>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest">Linked Branches</th>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-center whitespace-nowrap">Virtual Support</th>
                <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {appointmentTypes.map((item) => (
                <tr key={item.id} className="hover:bg-indigo-50/10 transition-all group">
                  <td className="px-6 py-3">
                    <input 
                      type="text" 
                      value={item.code} 
                      className="w-20 px-3 py-2 bg-slate-100/50 border border-slate-200 rounded-xl text-[10px] font-black text-indigo-700 uppercase tracking-tight focus:outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 focus:bg-white transition-all text-center"
                      onBlur={(e) => handleUpdate(item.id, { code: e.target.value })}
                      onChange={(e) => {
                         const val = e.target.value;
                         setAppointmentTypes(appointmentTypes.map(t => t.id === item.id ? { ...t, code: val } : t));
                      }}
                    />
                  </td>
                  <td className="px-6 py-3">
                    <button 
                        onClick={() => handleUpdate(item.id, { active: !item.active })}
                        className={`relative inline-flex h-5 w-10 items-center rounded-full transition-all duration-300 ${
                            item.active ? 'bg-indigo-500 shadow-[0_0_12px_rgba(79,70,229,0.3)]' : 'bg-slate-200'
                        }`}
                    >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                            item.active ? 'translate-x-5.5' : 'translate-x-1'
                        }`} />
                    </button>
                  </td>
                  <td className="px-6 py-3 min-w-[200px]">
                    <input 
                        type="text" 
                        value={item.typeName} 
                        className="w-full px-4 py-2.5 bg-white border border-transparent rounded-xl text-[13px] font-bold text-slate-700 focus:outline-none focus:border-indigo-100 focus:bg-indigo-50/10 transition-all group-hover:border-slate-100"
                        onBlur={(e) => handleUpdate(item.id, { typeName: e.target.value })}
                        onChange={(e) => {
                            const val = e.target.value;
                            setAppointmentTypes(appointmentTypes.map(t => t.id === item.id ? { ...t, typeName: val } : t));
                        }}
                    />
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex justify-center">
                        <button 
                            onClick={() => handleUpdate(item.id, { allBranches: !item.allBranches })}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border ${
                                item.allBranches 
                                ? 'bg-sky-50 text-sky-600 border-sky-100' 
                                : 'bg-slate-50 text-slate-400 border-slate-100'
                            }`}
                        >
                            <Building2 className="w-3 h-3" />
                            {item.allBranches ? 'Global' : 'Specific'}
                        </button>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap gap-1.5 max-w-[200px]">
                        {(item.branches || []).map((b, i) => (
                            <span key={i} className="inline-flex items-center px-2 py-1 rounded-lg bg-slate-100 text-[9px] font-black text-slate-500 border border-slate-200 animate-in zoom-in-95">
                                {b}
                                <X className="w-2.5 h-2.5 ml-1.5 cursor-pointer hover:text-red-500 transition-colors" 
                                    onClick={() => {
                                        const newBranches = item.branches.filter(branch => branch !== b);
                                        handleUpdate(item.id, { branches: newBranches });
                                    }}
                                />
                            </span>
                        ))}
                        <div className="relative group/add">
                            <select 
                                className="px-2 py-1 bg-white border border-slate-100 rounded-lg text-[9px] font-black text-indigo-400 focus:outline-none cursor-pointer hover:border-indigo-200 transition-all appearance-none"
                                onChange={(e) => {
                                    if (e.target.value !== 'Add') {
                                        const newBranches = [...(item.branches || []), e.target.value];
                                        handleUpdate(item.id, { branches: [...new Set(newBranches)] });
                                        e.target.value = 'Add';
                                    }
                                }}
                            >
                                <option value="Add">+ Add</option>
                                <option value="Main">Main Branch</option>
                                <option value="Branch A">Branch A</option>
                                <option value="Branch B">Branch B</option>
                            </select>
                        </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex justify-center">
                        <button 
                            onClick={() => handleUpdate(item.id, { virtual: !item.virtual })}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border ${
                                item.virtual 
                                ? 'bg-purple-50 text-purple-600 border-purple-100' 
                                : 'bg-slate-50 text-slate-400 border-slate-100'
                            }`}
                        >
                            <Video className={`w-3 h-3 ${item.virtual ? 'text-purple-500' : 'text-slate-300'}`} />
                            {item.virtual ? 'Telemedicine' : 'Office Only'}
                        </button>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => deleteType(item.id)}
                        className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {appointmentTypes.length === 0 && (
              <div className="py-20 text-center space-y-3">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto text-slate-200">
                      <Activity className="w-8 h-8" />
                  </div>
                  <div className="text-slate-300 font-black uppercase tracking-widest text-[10px]">No session types defined</div>
              </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentTypeSetup;
